"use client";

import type { ReactNode } from "react";
import { Container, Section, SectionHeader } from "@/components/ui/Container";
import { ModuleGrid } from "@/components/modules/ModuleCard";
import { CourseHero } from "@/components/modules/CourseHero";
import { CourseIncludedCard } from "@/components/modules/CourseIncludedCard";
import { CourseStartCard } from "@/components/modules/CourseStartCard";
import { CourseView } from "@/components/modules/CourseView";
import { PaidProcessingBanner } from "@/components/modules/PaidProcessingBanner";
import { flattenCurriculum, publishedSections } from "@/components/modules/lessonNav";
import { extractFirstYouTube, makeYouTubeEmbed } from "@/lib/embeds/youtube";
import { langPath, type Lang } from "@/lib/i18n";
import { modulePosterAlt } from "@/lib/lecturehub/poster";
import { useDict } from "@/components/i18n/DictProvider";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import type { SharePayload } from "./moduleShare";
import { isFreeToUnlock } from "./paywall";

export function CourseDetailClient({
  topSlot,
  sideSlot,
  lang,
  moduleId,
  slug,
  module: m,
  posterUrl,
  enrolmentIds,
  isAuthed,
  isSubscription,
  share = null,
  library = false,
  cfHash = null,
}: {
  /** The template's `content_top` placement — rendered at the top of the content column. */
  topSlot?: ReactNode;
  /** The template's `content_right` placement — rendered in the card column, under the card. */
  sideSlot?: ReactNode;
  lang: Lang;
  moduleId: number;
  slug: string;
  module: ModuleDetail;
  /** Cover image URL (resolved server-side via modulePoster) for the start card. */
  posterUrl: string | null;
  enrolmentIds: string[];
  isAuthed: boolean;
  isSubscription: boolean;
  share?: SharePayload | null;
  /** Rendered on the in-app /library surface (breadcrumb + similar links). */
  library?: boolean;
  /** Portal.cloudflareAccountHash — threads down to hero/poster image URLs. */
  cfHash?: string | null;
}) {
  const t = useDict();
  // The marketing header overlays the dark hero banner only for guests on the
  // public surface (authed users + /library render under the solid AppShell).
  const overlayHeader = !isAuthed && !library;
  const enrolledIds = new Set(enrolmentIds);
  const enrolled = enrolledIds.has(String(m.id));
  const rows = flattenCurriculum(m);
  const firstLessonId = rows[0]?.lesson.id ?? null;
  const lessonCount = rows.length;
  const sectionCount = publishedSections(m.course).length;
  const courseHref = langPath(lang, `/courses/${moduleId}/${m.slug ?? slug}`);

  // `m.refType` is `course` here by construction: the route resolver
  // (lib/routes/resolve-detail.ts) 308s a wrong-type id to the route that
  // owns it before this renders, on the public and the /library surface.
  // A "not my type" fallback used to live here; it was unreachable.
  const { videoId, htmlWithoutVideo } = extractFirstYouTube(m.course?.courseIntro ?? null);
  const mForView =
    videoId && m.course
      ? { ...m, course: { ...m.course, courseIntro: htmlWithoutVideo || null } }
      : m;

  return (
    <>
      <CourseHero
        lang={lang}
        module={m}
        library={library}
        overlayHeader={overlayHeader}
        cfHash={cfHash}
      />
      <Section className="!pt-5 !pb-16">
        <Container>
          <PaidProcessingBanner moduleId={moduleId} canView={m.canView === true} />

          {/* No top margin: the Section's !pt-5 gives the same 20px below the
              breadcrumb as above it (matches Ott/Podcast/Event details). The
              paid-processing banner, when it renders, brings its own mb. */}
          <div className="grid lg:grid-cols-[1fr_360px] gap-6 lg:gap-8 items-start animate-in fade-in duration-500 [animation-timing-function:var(--ease-smooth)]">
            <div className="min-w-0 grid gap-6">
              {topSlot}
              {videoId ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: makeYouTubeEmbed(videoId, { margins: false }),
                  }}
                />
              ) : null}
              {/* Abstract is rendered inside CourseView's "About this course"
                section as a deck above the description — they belong to the
                same conceptual block. See CourseView for the visual treatment. */}
              <CourseView module={mForView} lang={lang} courseHref={courseHref} />
            </div>
            {/* Right column: stack the enrol card + "what's included" card as
              one sticky unit. Sticky moved off CourseStartCard's inner aside
              to here so both children pin and release together — sticky on
              an inner sibling would let the unpinned IncludedCard scroll
              over the pinned StartCard. */}
            <div className="grid gap-4 lg:sticky lg:top-[calc(var(--marketing-header-h)+1rem)]">
              <CourseStartCard
                share={share}
                lang={lang}
                moduleId={moduleId}
                courseSlug={m.slug}
                firstLessonId={firstLessonId}
                free={isFreeToUnlock(m.price)}
                accessLevel={m.accessLevel}
                price={m.price ?? null}
                posterUrl={posterUrl}
                posterAlt={modulePosterAlt(m, m.name)}
                level={m.course?.courseLevel ?? null}
                lessonCount={lessonCount}
                sectionCount={sectionCount}
                canView={m.canView !== false}
                enrolled={enrolled}
                isAuthed={isAuthed}
                isSubscription={isSubscription}
                initialLiked={m.liked === true}
                createdAt={m.createdAt}
              />
              <CourseIncludedCard html={m.course?.courseFeatures ?? null} />
              {sideSlot}
            </div>
          </div>

          {m.similarModules?.length ? (
            <div className="mt-16 pt-12 border-t border-border">
              <SectionHeader title={t.detail.similar} />
              <ModuleGrid
                modules={m.similarModules.slice(0, 6)}
                lang={lang}
                t={t}
                enrolledIds={enrolledIds}
                library={library}
              />
            </div>
          ) : null}
        </Container>
      </Section>
    </>
  );
}
